import { useMutation, useQuery } from "@tanstack/react-query";
import { useAdminApplication, useAdminSession } from "../../app/providers";
import type { Schemas } from "../workspace/api.generated";
import { archiveContent, createContent, deleteContent, getContent, getContents, publishContent, updateContent } from "./api";
import type { ContentListQuery } from "./api";

export const contentKeys = {
  all: (epoch: number) => ["admin", epoch, "contents"] as const,
  lists: (epoch: number) => [...contentKeys.all(epoch), "list"] as const,
  list: (epoch: number, query: ContentListQuery) => [...contentKeys.lists(epoch), query] as const,
  detail: (epoch: number, id: number) => [...contentKeys.all(epoch), "detail", id] as const,
};

export function useContentList(query: ContentListQuery) {
  const { apiClient } = useAdminApplication();
  const session = useAdminSession();
  return useQuery({ queryKey: contentKeys.list(session.sessionEpoch, query), queryFn: ({ signal }) => getContents(apiClient, query, signal), staleTime: 0 });
}

export function useContentDetail(id?: number) {
  const { apiClient } = useAdminApplication();
  const session = useAdminSession();
  return useQuery({ queryKey: contentKeys.detail(session.sessionEpoch, id ?? 0), queryFn: ({ signal }) => getContent(apiClient, id!, signal), enabled: id !== undefined, staleTime: 0 });
}

export type ContentMutation =
  | { readonly kind: "create"; readonly body: Schemas["SaveContentRequest"] }
  | { readonly kind: "update"; readonly id: number; readonly body: Schemas["SaveContentRequest"] }
  | { readonly kind: "publish"; readonly id: number }
  | { readonly kind: "archive"; readonly id: number }
  | { readonly kind: "delete"; readonly id: number; readonly body: Schemas["DeleteContentRequest"] };

export function useContentMutation() {
  const { apiClient, queryClient, sessionLifecycle } = useAdminApplication();
  const session = useAdminSession();
  return useMutation({
    mutationFn: async (input: ContentMutation) => {
      const request = sessionLifecycle.createRequestController();
      try {
        switch (input.kind) {
          case "create": return await createContent(apiClient, input.body, request.controller.signal);
          case "update": return await updateContent(apiClient, input.id, input.body, request.controller.signal);
          case "publish": return await publishContent(apiClient, input.id, request.controller.signal);
          case "archive": return await archiveContent(apiClient, input.id, request.controller.signal);
          case "delete": return await deleteContent(apiClient, input.id, input.body, request.controller.signal);
        }
      } finally { request.release(); }
    },
    onSuccess: async (result, input) => {
      if (input.kind !== "delete") {
        const saved = result as Schemas["ContentPostView"];
        queryClient.setQueryData(contentKeys.detail(session.sessionEpoch, saved.id), saved);
      } else {
        queryClient.removeQueries({ queryKey: contentKeys.detail(session.sessionEpoch, input.id), exact: true });
      }
      await queryClient.invalidateQueries({ queryKey: contentKeys.lists(session.sessionEpoch), refetchType: "none" });
    },
  });
}
